import React, { useState, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { AlignLeft, AlignCenter, AlignRight, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Hook to manage recent colors in localStorage
const useRecentColors = (maxColors = 5) => {
  const [recentColors, setRecentColors] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('dekalio_recent_colors');
    if (stored) {
      try {
        setRecentColors(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent colors', e);
      }
    }
  }, []);

  const addRecentColor = (color) => {
    setRecentColors(prev => {
      // Remove if already exists
      const filtered = prev.filter(c => c.toLowerCase() !== color.toLowerCase());
      // Add to beginning
      const updated = [color.toUpperCase(), ...filtered].slice(0, maxColors);
      // Save to localStorage
      localStorage.setItem('dekalio_recent_colors', JSON.stringify(updated));
      return updated;
    });
  };

  return { recentColors, addRecentColor };
};

const FONTS = [
  { name: 'Arial', label: 'Arial', style: 'font-sans' },
  { name: 'Times New Roman', label: 'Times', style: 'font-serif' },
  { name: 'Comic Sans MS', label: 'Comic', style: 'font-comic' },
  { name: 'Impact', label: 'Impact', style: 'font-impact' },
  { name: 'Permanent Marker', label: 'Marker', style: 'font-marker' },
  { name: 'Dancing Script', label: 'Script', style: 'font-script' },
];

const TextEditor = ({ onTextAdd, selectedText, onTextUpdate }) => {
  const [text, setText] = useState('');
  const [font, setFont] = useState('Arial');
  const [color, setColor] = useState('#000000');
  const [tempColor, setTempColor] = useState('#000000');
  const [align, setAlign] = useState('left');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const { recentColors, addRecentColor } = useRecentColors();

  useEffect(() => {
    if (selectedText) {
      console.log('Setting text editor state from selectedText:', selectedText);
      setText(selectedText.text);
      setFont(selectedText.font);
      setColor(selectedText.color);
      setTempColor(selectedText.color);
      setAlign(selectedText.align);
    } else {
      setText('');
      setFont('Arial');
      setColor('#000000');
      setTempColor('#000000');
      setAlign('left');
    }
  }, [selectedText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;

    const textConfig = {
      text: text.trim(),
      font,
      color,
      align
    };

    console.log('TextEditor submitting:', textConfig);
    if (selectedText) {
      onTextUpdate(textConfig);
    } else {
      onTextAdd(textConfig);
    }
  };

  const handlePreviewUpdate = (previewConfig) => {
    if (selectedText) {
      onTextUpdate({
        ...previewConfig,
        isPreview: true
      });
    }
  };

  const handleTempColorChange = (newColor) => {
    setTempColor(newColor);
    handlePreviewUpdate({
      text: text.trim(),
      font,
      color: newColor,
      align
    });
  };

  const handleHexInputChange = (e) => {
    const value = e.target.value;
    
    // Validate hex color (with or without #)
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexPattern.test(value)) {
      const normalizedHex = value.startsWith('#') ? value : `#${value}`;
      handleTempColorChange(normalizedHex);
    } else {
      // Still update the display even if invalid
      setTempColor(value);
    }
  };

  const handleColorPickerClose = () => {
    setIsColorPickerOpen(false);
    setColor(tempColor);
    addRecentColor(tempColor); // Save to recent colors
    
    if (selectedText) {
      onTextUpdate({
        text: text.trim(),
        font,
        color: tempColor,
        align,
        isPreview: false,
        keepEditMode: true
      });
    }
  };

  const handleRecentColorClick = (recentColor) => {
    setTempColor(recentColor);
    setColor(recentColor);
    setIsColorPickerOpen(false);
    
    if (selectedText) {
      onTextUpdate({
        text: text.trim(),
        font,
        color: recentColor,
        align,
        isPreview: false,
        keepEditMode: true
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Font Selection */}
      <div className="space-y-2">
        <Label>Font</Label>
        <Select value={font} onValueChange={setFont}>
          <SelectTrigger>
            <SelectValue placeholder="Välj font" />
          </SelectTrigger>
          <SelectContent>
            {FONTS.map(f => (
              <SelectItem key={f.name} value={f.name} style={{ fontFamily: f.name }}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Text Input */}
      <div className="space-y-2">
        <Label>Text</Label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ fontFamily: font }}
          rows={2}
          placeholder="Skriv din text här..."
        />
      </div>

      {/* Color Picker */}
      <div className="space-y-2">
        <Label>Text färg</Label>
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
            onClick={(e) => {
              e.preventDefault();
              setIsColorPickerOpen(!isColorPickerOpen);
            }}
          >
            <div 
              className="w-6 h-6 rounded-full border border-black/20 mr-2"
              style={{ backgroundColor: isColorPickerOpen ? tempColor : color }}
            />
            <span>{isColorPickerOpen ? tempColor : color}</span>
          </Button>
          
          {isColorPickerOpen && (
            <div className="absolute left-0 top-full mt-2 z-50">
              <div 
                className="fixed inset-0" 
                onClick={handleColorPickerClose}
              />
              <div className="relative bg-background border rounded-lg shadow-xl p-3 space-y-3">
                <HexColorPicker 
                  color={tempColor} 
                  onChange={handleTempColorChange}
                />
                <Input
                  type="text"
                  value={tempColor}
                  onChange={handleHexInputChange}
                  placeholder="#000000"
                  className="font-mono text-sm"
                />
                
                {recentColors.length > 0 && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-1 mb-2">
                      <Clock size={12} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Senast använda</span>
                    </div>
                    <TooltipProvider>
                      <div className="flex gap-2 flex-wrap">
                        {recentColors.map((recentColor) => (
                          <Tooltip key={recentColor}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => handleRecentColorClick(recentColor)}
                                className="w-8 h-8 rounded-md border-2 border-input transition-all hover:scale-110 hover:border-primary"
                                style={{ backgroundColor: recentColor }}
                                title={recentColor}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-mono text-xs">{recentColor}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Text Button */}
      <Button
        type="submit"
        disabled={!text.trim()}
        className="w-full"
      >
        {selectedText ? 'Uppdatera text' : 'Lägg till text'}
      </Button>
    </form>
  );
};

export default TextEditor; 